import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  UserGroupIcon,
  PhoneIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const UserDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followupReminders, setFollowupReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Auto-refresh data every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getUserDashboard();
      setDashboardData(response.data);

      // Extract followup reminders from this week followups
      if (response.data.thisWeekFollowups) {
        setFollowupReminders(response.data.thisWeekFollowups.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'lead':
        return UserGroupIcon;
      case 'customer':
        return UsersIcon;
      case 'followup':
        return PhoneIcon;
      default:
        return UserGroupIcon;
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - new Date(date)) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getTimeUntilFollowup = (scheduledDate) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffInMinutes = Math.floor((scheduled - now) / (1000 * 60));

    if (diffInMinutes < 0) return 'Overdue';

    if (diffInMinutes < 60) return `In ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `In ${Math.floor(diffInMinutes / 60)}h`;

    const diffInDays = Math.floor(diffInMinutes / 1440);
    return `In ${diffInDays}d`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading your dashboard..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 p-8 mb-8 shadow-2xl"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

            <div className="relative z-10 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <ChartBarIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white mb-2">
                    Welcome back, {user?.firstName}!
                  </h1>
                  <p className="text-purple-100 text-lg">
                    Here's your personal dashboard overview
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Overview Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            {[
              {
                title: 'My Leads',
                value: dashboardData?.overview?.myLeads || 0,
                icon: UserGroupIcon,
                color: 'from-green-500 to-emerald-500',
                link: '/leads'
              },
              {
                title: 'My Customers',
                value: dashboardData?.overview?.myCustomers || 0,
                icon: UsersIcon,
                color: 'from-blue-500 to-cyan-500',
                link: '/customers'
              },
              {
                title: 'Pending Follow-ups',
                value: dashboardData?.overview?.myFollowups || 0,
                icon: PhoneIcon,
                color: 'from-orange-500 to-red-500',
                link: '/followups'
              },
              {
                title: 'Overdue Follow-ups',
                value: dashboardData?.overview?.overdueFollowups || 0,
                icon: ExclamationTriangleIcon,
                color: 'from-red-500 to-pink-500',
                link: '/followups'
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300"
              >
                <Link to={stat.link} className="block">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Today's Follow-ups */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <CalendarDaysIcon className="h-6 w-6 mr-2" />
                  Today's Follow-ups
                </h3>
              </div>

              <div className="p-6">
                {dashboardData?.todayFollowups?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboardData.todayFollowups.map((followup, index) => {
                      // Determine the link based on relatedTo type
                      const linkUrl = followup.relatedTo === 'lead' 
                        ? `/leads/${followup.relatedId._id}` 
                        : `/customers/${followup.relatedId._id}`;

                      return (
                        <motion.div
                          key={followup._id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.1 * index }}
                          className="group"
                        >
                          <Link 
                            to={linkUrl}
                            className="block p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="p-3 bg-blue-500 rounded-xl shadow-lg group-hover:bg-blue-600 transition-colors duration-300">
                                  <PhoneIcon className="h-6 w-6 text-white" />
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-lg font-bold text-gray-900 group-hover:text-blue-900 transition-colors duration-300">
                                    {followup.title}
                                  </h4>
                                  <p className="text-sm text-gray-700 font-medium">
                                    {followup.relatedId ? `${followup.relatedId.firstName} ${followup.relatedId.lastName}` : followup.relatedTo}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {new Date(followup.scheduledDate).toLocaleTimeString()}
                                  </p>
                                  <div className="flex items-center mt-2 space-x-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {followup.type}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {followup.relatedTo}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="flex flex-col items-end space-y-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg">
                                    Today
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Click to view details
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No follow-ups scheduled for today</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Upcoming Follow-ups */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <ClockIcon className="h-6 w-6 mr-2" />
                  Upcoming This Week
                </h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {followupReminders.length > 0 ? (
                  followupReminders.map((followup, index) => (
                    <motion.div
                      key={followup._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="p-4 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg">
                          <PhoneIcon className="h-4 w-4 text-orange-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{followup.title}</p>
                          <p className="text-sm text-gray-600">
                            {followup.relatedId ? `${followup.relatedId.firstName} ${followup.relatedId.lastName}` : followup.relatedTo}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-gray-500">
                              {new Date(followup.scheduledDate).toLocaleDateString()}
                            </p>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {getTimeUntilFollowup(followup.scheduledDate)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No upcoming follow-ups</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Leads by Stage */}
          {dashboardData?.leadsByStage && dashboardData.leadsByStage.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-500 to-blue-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white">My Leads by Stage</h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
  
                  {dashboardData.leadsByStage.map((stageData, index) => (
                    <motion.div
                      key={stageData.name}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.1 * index }}
                      className="rounded-xl p-4 text-center"
                      style={{ backgroundColor: `${stageData.color}20`, border: `2px solid ${stageData.color}` }}
                    >
                      <div className="text-2xl font-bold text-gray-900">{stageData.count}</div>
                      <div className="text-sm text-gray-600 capitalize">{stageData.name}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Performance Goals */}
          {dashboardData?.activeGoals && dashboardData.activeGoals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <FlagIcon className="h-6 w-6 mr-2" />
                  My Performance Goals
                </h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData.activeGoals.map((goal, index) => {
                    const progress = (goal.currentValue / goal.targetValue) * 100;
                    const isCompleted = goal.status === 'completed';
                    const isExpiringSoon = goal.daysRemaining <= 3 && goal.daysRemaining > 0;

                    return (
                      <motion.div
                        key={goal._id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 * index }}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">{goal.title}</h4>
                            <p className="text-xs text-gray-500 capitalize">{goal.type}</p>
                          </div>
                          {isCompleted && (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                          )}
                          {isExpiringSoon && !isCompleted && (
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 flex-shrink-0" />
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{goal.currentValue}</span>
                            <span>{goal.targetValue}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${isCompleted ? 'bg-green-500' : progress >= 75 ? 'bg-blue-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                          <div className="text-right text-sm font-medium text-gray-900 mt-1">
                            {progress.toFixed(1)}%
                          </div>
                        </div>

                        <div className="text-xs text-gray-500">
                          {goal.daysRemaining > 0 ? (
                            `${goal.daysRemaining} days left`
                          ) : goal.daysRemaining === 0 ? (
                            'Expires today'
                          ) : (
                            'Expired'
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* Recent Activity */}
          {(dashboardData?.recentActivity?.leads?.length > 0 || dashboardData?.recentActivity?.customers?.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
                <h3 className="text-xl font-bold text-white">Recent Activity</h3>
              </div>

              <div className="divide-y divide-gray-200">
                {[
                  ...(dashboardData.recentActivity.leads || []).map(lead => ({
                    id: lead._id,
                    type: 'lead',
                    title: `Updated lead: ${lead.firstName} ${lead.lastName}`,
                    time: formatTimeAgo(lead.updatedAt),
                    icon: UserGroupIcon,
                    color: 'bg-green-100 text-green-600'
                  })),
                  ...(dashboardData.recentActivity.customers || []).map(customer => ({
                    id: customer._id,
                    type: 'customer',
                    title: `Updated customer: ${customer.firstName} ${customer.lastName}`,
                    time: formatTimeAgo(customer.updatedAt),
                    icon: UsersIcon,
                    color: 'bg-blue-100 text-blue-600'
                  }))
                ]
                  .sort((a, b) => new Date(b.time) - new Date(a.time))
                  .slice(0, 10)
                  .map((activity, index) => (
                    <motion.div
                      key={`${activity.type}-${activity.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="p-4"
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 p-2 rounded-lg ${activity.color}`}>
                          <activity.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                          <p className="text-sm text-gray-500">{activity.time}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default UserDashboard;